import React, { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./PrivacyPolicy.module.scss";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";

const PrivacyPolicy = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <div className={styles.container}>
        <div className={styles.content}>
          <Link to="/" className={styles.backButton}>
            <span className="material-icons">arrow_back</span>
            Back to Home
          </Link>

          <h1 className={styles.title}>Privacy Policy</h1>

          <section className={styles.section}>
            <h2>Introduction</h2>
            <p>
              This Privacy Policy describes how Vertik AIC Frontend ("we,"
              "our," or "us") collects, uses, and protects your information when
              you use our Gmail integration application (the "Service"). By
              using our Service, you agree to the collection and use of
              information in accordance with this policy.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Information We Collect</h2>

            <h3>1. Gmail Data</h3>
            <p>
              When you connect your Gmail account to our Service, we may access
              and process the following information:
            </p>
            <ul>
              <li>
                <strong>Email Messages:</strong> Subject lines, sender
                information, recipient information, message content, and
                timestamps
              </li>
              <li>
                <strong>Email Metadata:</strong> Message IDs, thread IDs,
                labels, read status, and importance markers
              </li>
              <li>
                <strong>Account Information:</strong> Your Gmail email address
                and profile information
              </li>
              <li>
                <strong>Email Statistics:</strong> Total message count, thread
                count, and account activity metrics
              </li>
            </ul>

            <h3>2. Authentication Information</h3>
            <ul>
              <li>
                <strong>OAuth Tokens:</strong> Access tokens and refresh tokens
                provided by Google for Gmail API access
              </li>
              <li>
                <strong>Session Data:</strong> Connection status and
                authentication state stored locally in your browser
              </li>
            </ul>

            <h3>3. Usage Data</h3>
            <ul>
              <li>
                <strong>Application Logs:</strong> Error logs, performance
                metrics, and debugging information
              </li>
              <li>
                <strong>User Interactions:</strong> Features used, pages
                visited, and application usage patterns
              </li>
              <li>
                <strong>Technical Data:</strong> Browser type, device
                information, and IP address
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>How We Use Your Information</h2>

            <h3>1. Core Service Functionality</h3>
            <ul>
              <li>
                <strong>Email Management:</strong> Display your Gmail inbox,
                read emails, and manage email threads
              </li>
              <li>
                <strong>Email Operations:</strong> Create drafts, send replies,
                and organize emails
              </li>
              <li>
                <strong>Search and Filter:</strong> Help you find and categorize
                emails based on content and metadata
              </li>
            </ul>

            <h3>2. Service Improvement</h3>
            <ul>
              <li>
                <strong>Performance Optimization:</strong> Analyze usage
                patterns to improve application performance
              </li>
              <li>
                <strong>Feature Development:</strong> Understand user needs to
                develop new features
              </li>
              <li>
                <strong>Bug Fixing:</strong> Use error logs to identify and
                resolve technical issues
              </li>
            </ul>

            <h3>3. Security and Compliance</h3>
            <ul>
              <li>
                <strong>Authentication:</strong> Verify your identity and
                maintain secure access to your Gmail account
              </li>
              <li>
                <strong>Data Protection:</strong> Implement security measures to
                protect your information
              </li>
              <li>
                <strong>Compliance:</strong> Ensure adherence to applicable
                privacy laws and regulations
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Data Storage and Security</h2>

            <h3>1. Local Storage</h3>
            <ul>
              <li>
                <strong>Browser Storage:</strong> Authentication tokens and
                session data are stored locally in your browser
              </li>
              <li>
                <strong>Temporary Data:</strong> Email content and metadata are
                cached temporarily for performance
              </li>
              <li>
                <strong>User Control:</strong> You can clear stored data at any
                time through your browser settings
              </li>
            </ul>

            <h3>2. Security Measures</h3>
            <ul>
              <li>
                <strong>Encryption:</strong> All data transmission uses HTTPS
                encryption
              </li>
              <li>
                <strong>Token Management:</strong> OAuth tokens are handled
                securely and can be revoked at any time
              </li>
              <li>
                <strong>Access Control:</strong> Only authorized personnel have
                access to system logs and error data
              </li>
            </ul>

            <h3>3. Data Retention</h3>
            <ul>
              <li>
                <strong>Session Data:</strong> Stored only while you're actively
                using the application
              </li>
              <li>
                <strong>Logs:</strong> Error logs and performance data are
                retained for up to 30 days
              </li>
              <li>
                <strong>User Data:</strong> No email content is permanently
                stored on our servers
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Third-Party Services</h2>

            <h3>1. Google Services</h3>
            <p>
              Our Service integrates with Google's Gmail API and Google Identity
              Services. When you use our Service:
            </p>
            <ul>
              <li>
                <strong>Google OAuth:</strong> You authorize our application to
                access your Gmail account
              </li>
              <li>
                <strong>Google's Privacy Policy:</strong> Google's privacy
                practices apply to your Gmail data
              </li>
              <li>
                <strong>API Access:</strong> We only access Gmail data necessary
                for the Service's functionality
              </li>
            </ul>

            <h3>2. Google API Services User Data Policy</h3>
            <p>
              We comply with Google's API Services User Data Policy, including:
            </p>
            <ul>
              <li>
                <strong>Limited Use:</strong> Gmail data is used only to provide
                the Service's core functionality
              </li>
              <li>
                <strong>No Selling:</strong> We do not sell, rent, or share your
                Gmail data with third parties
              </li>
              <li>
                <strong>No Advertising:</strong> Gmail data is not used for
                advertising purposes
              </li>
              <li>
                <strong>User Control:</strong> You can revoke access to your
                Gmail account at any time
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Your Rights and Choices</h2>

            <h3>1. Access and Control</h3>
            <ul>
              <li>
                <strong>Revoke Access:</strong> You can disconnect your Gmail
                account at any time
              </li>
              <li>
                <strong>Clear Data:</strong> You can clear locally stored data
                through your browser
              </li>
              <li>
                <strong>Account Deletion:</strong> You can request deletion of
                your account and associated data
              </li>
            </ul>

            <h3>2. Data Portability</h3>
            <ul>
              <li>
                <strong>Export Data:</strong> You can export your email data
                directly from Gmail
              </li>
              <li>
                <strong>Data Transfer:</strong> We do not store your email data
                permanently, so no transfer is needed
              </li>
            </ul>

            <h3>3. Communication Preferences</h3>
            <ul>
              <li>
                <strong>Updates:</strong> We may send you important updates
                about the Service
              </li>
              <li>
                <strong>Support:</strong> We may contact you regarding technical
                support or service issues
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 13 years of age. We
              do not knowingly collect personal information from children under
              13. If you are a parent or guardian and believe your child has
              provided us with personal information, please contact us
              immediately.
            </p>
          </section>

          <section className={styles.section}>
            <h2>International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries
              other than your own. We ensure that such transfers comply with
              applicable data protection laws and implement appropriate
              safeguards to protect your information.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by:
            </p>
            <ul>
              <li>Posting the new Privacy Policy on this page</li>
              <li>
                Updating the "Last Updated" date at the top of this policy
              </li>
              <li>Sending email notifications for significant changes</li>
            </ul>
            <p>
              Your continued use of the Service after any changes constitutes
              acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Compliance and Legal Basis</h2>

            <h3>1. Legal Basis for Processing (GDPR)</h3>
            <ul>
              <li>
                <strong>Consent:</strong> You provide explicit consent when
                connecting your Gmail account
              </li>
              <li>
                <strong>Legitimate Interest:</strong> Processing is necessary
                for providing the Service
              </li>
              <li>
                <strong>Contract Performance:</strong> Processing is necessary
                to fulfill our service agreement
              </li>
            </ul>

            <h3>2. Data Protection Rights (GDPR)</h3>
            <p>
              If you are in the European Union, you have the following rights:
            </p>
            <ul>
              <li>
                <strong>Right of Access:</strong> Request copies of your
                personal data
              </li>
              <li>
                <strong>Right to Rectification:</strong> Request correction of
                inaccurate data
              </li>
              <li>
                <strong>Right to Erasure:</strong> Request deletion of your
                personal data
              </li>
              <li>
                <strong>Right to Restrict Processing:</strong> Request
                limitation of data processing
              </li>
              <li>
                <strong>Right to Data Portability:</strong> Request transfer of
                your data
              </li>
              <li>
                <strong>Right to Object:</strong> Object to processing of your
                personal data
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Contact Information</h2>
            <p>
              If you have any questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className={styles.contactInfo}>
              <p>
                <strong>Email:</strong> privacy@vertik.ai
              </p>
              <p>
                <strong>Address:</strong> Vertik AIC Frontend Privacy Team
              </p>
              <p>
                <strong>Response Time:</strong> We will respond to your inquiry
                within 30 days
              </p>
            </div>
          </section>

          <section className={styles.section}>
            <h2>Additional Resources</h2>
            <ul className={styles.resourceLinks}>
              <li>
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google API Services User Data Policy
                </a>
              </li>
              <li>
                <a
                  href="https://developers.google.com/gmail/api/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Gmail API Terms
                </a>
              </li>
            </ul>
          </section>

          <div className={styles.footer}>
            <div className={styles.meta}>
              <p>
                <strong>Effective Date:</strong> January 15, 2025
              </p>
              <p>
                <strong>Last Updated:</strong> January 15, 2025
              </p>
            </div>
            <p>
              This Privacy Policy is effective as of January 15, 2025, and will
              remain in effect except with respect to any changes in its
              provisions in the future, which will be in effect immediately
              after being posted on this page.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
